type ScopeLike = {
  setTag?: (key: string, value: string) => void;
  setContext?: (key: string, value: Record<string, unknown>) => void;
  setLevel?: (value: string) => void;
};

type ConfigureScope = (scope: ScopeLike) => void;

const REDACTED = "[REDACTED]";
const sensitiveKeyPattern = /(authorization|cookie|password|password_hash|token|jwt|serial_number)/i;

let sentryModulePromise: Promise<any | null> | null = null;
let clientInitialized = false;
let serverInitialized = false;

const getSentry = () => {
  if (!sentryModulePromise) {
    sentryModulePromise = import("@sentry/nextjs")
      .then((mod) => mod?.default ?? mod)
      .catch(() => null);
  }
  return sentryModulePromise;
};

const sanitizeDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeDeep(item));
  }
  if (!value || typeof value !== "object") {
    return value;
  }

  const source = value as Record<string, unknown>;
  const sanitized: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(source)) {
    if (sensitiveKeyPattern.test(key)) {
      sanitized[key] = REDACTED;
      continue;
    }
    sanitized[key] = sanitizeDeep(entry);
  }
  return sanitized;
};

const sanitizeEvent = (event: any) => {
  const clone = {
    ...event,
    request: event?.request ? { ...event.request } : undefined,
    user: event?.user ? { ...event.user } : undefined,
  };

  if (clone.request?.headers) {
    clone.request.headers = sanitizeDeep(clone.request.headers);
  }
  if (clone.request?.data) {
    clone.request.data = sanitizeDeep(clone.request.data);
  }
  if (clone.extra) {
    clone.extra = sanitizeDeep(clone.extra);
  }
  if (clone.user) {
    delete clone.user.email;
    delete clone.user.ip_address;
  }

  return clone;
};

export const initClientSentry = () => {
  if (clientInitialized) {
    return;
  }
  clientInitialized = true;

  void getSentry().then((Sentry) => {
    if (!Sentry?.init) {
      return;
    }

    Sentry.init({
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
      environment: process.env.SENTRY_ENVIRONMENT ?? process.env.NODE_ENV ?? "development",
      release: process.env.SENTRY_RELEASE,
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: false,
      beforeSend: (event: unknown) => sanitizeEvent(event),
    });
  });
};

export const initServerSentry = () => {
  if (serverInitialized) {
    return;
  }
  serverInitialized = true;

  void getSentry().then((Sentry) => {
    if (!Sentry?.init) {
      return;
    }

    Sentry.init({
      dsn: process.env.SENTRY_DSN ?? process.env.NEXT_PUBLIC_SENTRY_DSN,
      environment: process.env.SENTRY_ENVIRONMENT ?? process.env.NODE_ENV ?? "development",
      release: process.env.SENTRY_RELEASE,
      tracesSampleRate: 0.2,
      sendDefaultPii: false,
      beforeSend: (event: unknown) => sanitizeEvent(event),
    });
  });
};

export const captureException = (error: unknown, configureScope?: ConfigureScope) => {
  if (!error) {
    return;
  }

  void getSentry().then((Sentry) => {
    if (!Sentry?.captureException) {
      return;
    }

    if (configureScope && typeof Sentry.withScope === "function") {
      Sentry.withScope((scope: ScopeLike) => {
        configureScope(scope);
        Sentry.captureException(error);
      });
      return;
    }

    Sentry.captureException(error);
  });
};

export const captureMessage = (
  message: string,
  level: "fatal" | "error" | "warning" | "log" | "info" | "debug" = "info",
  configureScope?: ConfigureScope,
) => {
  if (!message) {
    return;
  }

  void getSentry().then((Sentry) => {
    if (!Sentry?.captureMessage) {
      return;
    }

    if (configureScope && typeof Sentry.withScope === "function") {
      Sentry.withScope((scope: ScopeLike) => {
        configureScope(scope);
        Sentry.captureMessage(message, level);
      });
      return;
    }

    Sentry.captureMessage(message, level);
  });
};
